import moment from 'moment';

import api, { request } from '../../../api';
import { ICreateSeasonForm } from '../../../scenes/admin/Settings/ClubSettings/SeasonsSection/NewSeason/NewSeason';
import { IErrorsStore } from '../../errors/store';
import { IActionGroup, IStore } from '../../index';
import {
  IAdminClub,
  IAdminClubsStore,
  IClubCreationForm,
  IClubOfficer,
  IClubOfficersStore,
  IClubTransaction,
  INewClubOfficerForm
} from './store';

export interface INotificationMessage {
  club?: number;
  event?: number;
  message: string;
}

export interface ITransactionRefund {
  refundAmount: number;
  refundReason?: string;
}

export interface ISubmitRefundArgs {
  clubId: number;
  transactionId: number;
  refund: ITransactionRefund;
}

export interface IClubSettings {
  course?: number;
  description?: string;
  email?: string;
  name?: string;
  membershipPrice?: number;
  owner?: number;
  processPayment?: boolean;
  profileImage?: string;
}

export type ICreateManagement = (
  newClubOfficerData: INewClubOfficerForm
) => { response: any; errors?: IErrorsStore };

export interface IAdminClubActions extends IActionGroup {
  all: () => { adminClubs?: IAdminClubsStore; errors?: IErrorsStore };
  create: (data: IClubCreationForm) => { adminClubs?: IAdminClubsStore; errors?: IErrorsStore };
  createManagement: ICreateManagement;
}

export interface ICreateSeason {
  createSeason: (
    data: ICreateSeasonForm,
    clubID: number
  ) => { season?: IAdminClubsStore; errors?: IErrorsStore };
}

export type ISubmitRefund = (props: ISubmitRefundArgs) => { response?: any; errors?: IErrorsStore };

export type TGetAllClubsAction = (
  clubId: number
) => { adminClubs?: IAdminClubsStore; errors?: IErrorsStore };

export interface IAdminClubActions extends IActionGroup, ICreateSeason {
  all: () => { adminClubs?: IAdminClubsStore; errors?: IErrorsStore };
  create: (data: IClubCreationForm) => { adminClubs?: IAdminClubsStore; errors?: IErrorsStore };
  delete: (clubId: number) => { adminClubs?: IAdminClubsStore; errors?: IErrorsStore };
  edit: (
    clubId: number,
    data: IClubSettings
  ) => { adminClubs?: IAdminClubsStore; errors?: IErrorsStore };
  editImage: (
    clubId: number,
    image: string,
    type: string
  ) => { adminClubs?: IAdminClubsStore; errors?: IErrorsStore };
  get: TGetAllClubsAction;
  getTransactions: (clubId: number) => { response: IClubTransaction[]; errors?: IErrorsStore };
  getManagement: (
    clubId: number,
    forceUpdate?: boolean
  ) => { adminClubs?: IAdminClubsStore; errors?: IErrorsStore };
  removeManagement: (
    clubId: number,
    managementId: number
  ) => { response: any; errors?: IErrorsStore };
  sendNotification: (props: INotificationMessage) => { response?: any; errors?: IErrorsStore };
  submitRefund: ISubmitRefund;
  updateManagement: (
    clubId: number,
    managementId: number,
    data: Partial<IClubOfficer>
  ) => { response: any; errors?: IErrorsStore };
}

const actions: IActionGroup = {
  all: async (state: IStore) => {
    if (Object.keys(state.adminClubs.all).length) {
      return {
        adminClubs: state.adminClubs
      };
    }

    const response = await request.get(`${api.admin.clubs}`);

    if (response.error) {
      return { errors: { adminClubs: { msg: 'Error retrieving the club list.' } } };
    }

    // store into [clubId]: club format
    const allClubs = response.reduce(
      (parsedClubs: IAdminClubsStore, rawClub: IAdminClub) => ({
        ...parsedClubs,
        [rawClub.id]: rawClub
      }),
      {}
    );

    return {
      adminClubs: {
        all: allClubs
      }
    };
  },
  create: async (state: IStore, data: IClubCreationForm) => {
    const response = await request.post(api.admin.clubs, {
      membershipPrice: 0,
      name: data.name
    });
    if (response.error) {
      const key = Object.keys(response.error.body)[0];
      return {
        errors: {
          auth: {
            el: key,
            msg: response.error.body[key]
          }
        }
      };
    }

    return {
      adminClubs: {
        ...state.adminClubs,
        all: {
          ...state.adminClubs.all,
          [response.id]: response
        },
        current: response,
        [response.id]: response
      }
    };
  },
  createSeason: async (state: IStore, data: ICreateSeasonForm, clubId: number) => {
    const season = {
      clubId,
      end: moment(data.end).format('YYYY-MM-DDTHH:mm:ssZ'),
      start: moment(data.start).format('YYYY-MM-DDTHH:mm:ssZ')
    };

    const response = await request.post(`${api.admin.clubs}${clubId}/seasons`, season);

    if (response.error && !clubId) {
      return { errors: { club: { msg: 'Club not found' } } };
    }

    if (response.error) {
      return {
        errors: {
          season: {
            msg: response.error.statusText
              ? response.error.statusText[0]
              : 'Error creating the season'
          }
        }
      };
    }

    return {
      seasons: {
        ...state.seasons,
        [response.id]: response,
        [clubId]: {
          ...state.seasons[clubId],
          seasons: state.seasons[clubId] ? [...state.seasons[clubId].seasons, response] : [response]
        },
        all: {
          ...state.seasons.all,
          [response.id]: response
        }
      }
    };
  },

  delete: async (state: IStore, clubId: number) => {
    const response = await request.delete(`${api.admin.clubs}${clubId}/`);
    if (response.error) {
      return { errors: { adminClubs: { msg: 'There was an error deleting the club' } } };
    }

    return {
      adminClubs: {
        ...state.adminClubs,
        all: {
          ...state.adminClubs.all
        }
      }
    };
  },
  edit: async (state: IStore, clubId: number, data: IClubSettings) => {
    const body: any = {
      ...data
    };

    const response = await request.patch(`${api.admin.clubs}${clubId}/`, body);
    if (response.error) {
      return { errors: { adminClubs: { msg: 'Error updating the club' } } };
    }

    const newClub = {
      ...response
    };

    return {
      adminClubs: {
        ...state.adminClubs,
        all: {
          ...state.adminClubs.all,
          [response.id]: newClub
        },
        current: newClub,
        [response.id]: newClub
      }
    };
  },
  editImage: async (state: IStore, clubId: number, image: string, type: string) => {
    const regex = /data:image\/([a-zA-Z]+);/;
    const found = image.match(regex);
    const extension = found ? found[1] : 'png';

    const filename = String((Math.random() * 10000000000000).toFixed()) + `.${extension}`;
    const formData = new FormData();

    const res = await fetch(image);
    const blob = await res.blob();

    formData.append(
      type === 'profile_image' ? 'profile_image' : 'background_image',
      blob,
      filename
    );

    const response = await request.multipart(`${api.admin.clubs}${clubId}/`, formData);

    if (response.error) {
      return { errors: { adminClubs: { msg: 'Error updating the club image.' } } };
    }

    const newClub = {
      ...response
    };

    return {
      adminClubs: {
        ...state.adminClubs,
        all: {
          ...state.adminClubs.all,
          [response.id]: newClub
        },
        current: newClub,
        [response.id]: newClub
      }
    };
  },
  editSeason: async (
    state: IStore,
    data: ICreateSeasonForm,
    clubId: number,
    seasonId: string | number
  ) => {
    const season = {
      clubId,
      end: moment(data.end).format('YYYY-MM-DDTHH:mm:ssZ'),
      start: moment(data.start).format('YYYY-MM-DDTHH:mm:ssZ')
    };

    const response = await request.patch(`${api.admin.clubs}${clubId}/seasons/${seasonId}`, season);

    if (response.error && !clubId) {
      return { errors: { club: { msg: 'Club not found' } } };
    }

    if (response.error) {
      return {
        errors: {
          season: {
            msg: response.error.statusText
              ? response.error.statusText[0]
              : 'Error creating the season'
          }
        }
      };
    }

    const previousSeasons = state.seasons[clubId]
      ? state.seasons[clubId].seasons.filter(prevSeason => prevSeason.id !== seasonId)
      : [];

    return {
      seasons: {
        ...state.seasons,
        [clubId]: {
          ...state.seasons[clubId],
          seasons: [...previousSeasons, response]
        }
      }
    };
  },
  get: async (state: IStore, clubId: number) => {
    if (state.adminClubs[clubId] && state.adminClubs[clubId].hasOwnProperty('owner')) {
      return state;
    }

    const response = await request.get(`${api.admin.clubs}${clubId}/`);

    if (response.error) {
      return { errors: { adminClubs: { msg: 'Error retrieving the Club.' } } };
    }

    const club: IAdminClub = response;
    return {
      adminClubs: {
        [club.id]: club
      }
    };
  },

  createManagement: async (state: IStore, newClubOfficerData: INewClubOfficerForm) => {
    const { club: clubId } = newClubOfficerData;

    const response = await request.post(api.admin.clubManagement.get(clubId), newClubOfficerData);

    return response.error
      ? { errors: { adminClubs: { msg: 'Error setting membership.' } } }
      : {
          adminClubs: {
            ...state.adminClubs,
            management: {
              ...state.adminClubs.management,
              [clubId]: {
                ...state.adminClubs.management[clubId],
                [response.id]: { ...response, isOwner: false }
              }
            }
          }
        };
  },

  getManagement: async (state: IStore, clubId: number, forceUpdate?: boolean) => {
    if (state.adminClubs.management[clubId] && !forceUpdate) {
      return {
        adminClubs: state.adminClubs
      };
    }

    const response = await request.get(api.admin.clubManagement.get(clubId));

    if (response.length <= 0) {
      return state;
    }

    if (response.error) {
      return { errors: { adminClubs: { msg: 'Error retrieving the Club management.' } } };
    }

    const { owner } = state.adminClubs[clubId];

    const management: IClubOfficersStore = {};

    response.forEach((officer: IClubOfficer) => {
      management[officer.id] = {
        ...officer,
        isOwner: !!owner && officer.user.id === owner.id
      };
    });

    return {
      adminClubs: {
        management: {
          [clubId]: { ...management }
        }
      }
    };
  },

  getTransactions: async (state: IStore, clubId: number) => {
    if (state.adminClubs.transactions[clubId]) {
      return {
        adminClubs: state.adminClubs
      };
    }

    const response = await request.get(api.admin.clubTransactions(clubId));

    return response.error
      ? { errors: { ledger: { msg: 'Error getting transactions.' } } }
      : {
          adminClubs: {
            ...state.adminClubs,
            transactions: {
              ...state.adminClubs.transactions,
              [clubId]: response.reduce(
                (parsedTransactions: IClubTransaction[], transaction: IClubTransaction) => ({
                  ...parsedTransactions,
                  [transaction.id]: transaction
                }),
                {}
              )
            }
          }
        };
  },

  removeManagement: async (state: IStore, clubId: number, managementId: number) => {
    const response = await request.delete(api.admin.clubManagement.update(clubId, managementId));

    if (response.error) {
      return { errors: { adminClubs: { msg: 'Error retrieving the Club management.' } } };
    }

    delete state.adminClubs.management[clubId][managementId];

    return {
      adminClubs: {
        ...state.adminClubs,
        management: {
          [clubId]: state.adminClubs.management[clubId]
        }
      }
    };
  },
  sendNotification: async (state: IStore, props: INotificationMessage) => {
    await request.post(api.admin.notifications, props);

    return state;
  },

  submitRefund: async (state: IStore, props: ISubmitRefundArgs) => {
    const { clubId, transactionId, refund } = props;

    const response = await request.post(
      api.admin.clubTransactionRefunds(clubId, transactionId),
      refund
    );

    return response.error
      ? { errors: { ledger: { msg: 'Error submitting refund.' } } }
      : {
          adminClubs: {
            ...state.adminClubs,
            transactions: {
              ...state.adminClubs.transactions,
              [clubId]: {
                ...state.adminClubs.transactions[clubId],
                [response.id]: response
              }
            }
          }
        };
  },

  updateManagement: async (
    state: IStore,
    clubId: number,
    managementId: number,
    data: Partial<IClubOfficer>
  ) => {
    const response = await request.patch(
      api.admin.clubManagement.update(clubId, managementId),
      data
    );

    if (response.error) {
      return { errors: { adminClubs: { msg: 'Error retrieving the Club management.' } } };
    }

    const { owner } = state.adminClubs[clubId];

    return {
      adminClubs: {
        ...state.adminClubs,
        management: {
          [clubId]: {
            ...state.adminClubs.management[clubId],
            [response.id]: { ...response, isOwner: !!owner && response.user.id === owner.id }
          }
        }
      }
    };
  }
};

export default actions;
